import { useState, useEffect } from "react";
import crm from "./crmApi";
import { useCrm, Loader } from "./CrmApp";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { PageHeader, Card, Field } from "./ui";
import { PRESETS, TrendChart, Bars } from "./CrmDashboard";

export default function CrmReports() {
  const { user } = useCrm();
  const [settings, setSettings] = useState({ sources: [], statuses: [], priorities: [] });
  const [perf, setPerf] = useState(null);
  const [dash, setDash] = useState(null);
  const [preset, setPreset] = useState("last30");
  const [f, setF] = useState({ source: "", status: "", city: "", priority: "" });
  const [busy, setBusy] = useState(false);
  const canSeePerf = ["owner", "sales_manager"].includes(user.role);

  useEffect(() => { crm.settings().then(setSettings).catch(() => {}); }, []);
  useEffect(() => { setDash(null); crm.dashboard({ preset }).then(setDash).catch(() => {}); }, [preset]);
  useEffect(() => { if (canSeePerf) crm.performance().then(setPerf).catch(() => {}); }, [canSeePerf]);

  const download = () => { setBusy(true); crm.exportCsv(Object.fromEntries(Object.entries(f).filter(([, v]) => v))).catch(() => toast.error("Export failed")).finally(() => setBusy(false)); };

  return (
    <div className="space-y-4 crm-fade">
      <PageHeader title="Reports" sub="Lead trends, team performance and exports." actions={<select value={preset} onChange={(e) => setPreset(e.target.value)} data-testid="reports-preset" className="crm-in w-auto" aria-label="Date range">{PRESETS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>} />
      <Card title="Leads Trend" testid="reports-trend">{!dash ? <Loader /> : <TrendChart trend={dash.trend} height={240} />}</Card>

      {canSeePerf && (
        <Card title="Employee Performance" bodyClass="" testid="reports-performance">
          {!perf ? <Loader /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead><tr className="text-left border-b border-[#E4EBE4]">
                  {["Employee", "Role", "Leads", "Contacted", "Qualified", "Lost", "Conv %", "Calls", "Tasks Done", "Pending"].map((h, i) => <th key={h} className={`px-3 ${i === 0 ? "pl-4" : ""} ${i > 1 ? "text-right" : ""}`}>{h}</th>)}
                </tr></thead>
                <tbody className="divide-y divide-[#E4EBE4] tabular-nums">
                  {perf.items.map((e) => (
                    <tr key={e.id} data-testid={`perf-row-${e.id}`}>
                      <td className="pl-4 px-3 font-semibold whitespace-nowrap">{e.name}</td>
                      <td className="px-3 capitalize text-[#5F6E64] whitespace-nowrap">{e.role.replace("_", " ")}</td>
                      <td className="px-3 text-right">{e.leads}</td><td className="px-3 text-right">{e.contacted}</td>
                      <td className="px-3 text-right text-[#047857] font-semibold">{e.qualified}</td>
                      <td className="px-3 text-right text-[#B91C1C]">{e.lost}</td>
                      <td className="px-3 text-right font-semibold text-[#236B45]">{e.conversion}%</td>
                      <td className="px-3 text-right">{e.calls}</td><td className="px-3 text-right">{e.tasks_done}</td><td className="px-3 pr-4 text-right">{e.tasks_pending}</td>
                    </tr>
                  ))}
                  {perf.items.length === 0 && <tr><td colSpan="10" className="p-6 text-[#5F6E64] text-center">No team members found.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="Lost Reasons">{!dash ? <Loader /> : <Bars data={dash.lost_reasons || []} color="#DC2626" emptyText="No lost leads in scope." />}</Card>
        <Card title="Export Lead Register (CSV)">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <Field label="Status"><select className="crm-in" data-testid="export-status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}><option value="">All statuses</option>{settings.statuses.map((s) => <option key={s}>{s}</option>)}</select></Field>
            <Field label="Source"><select className="crm-in" data-testid="export-source" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })}><option value="">All sources</option>{settings.sources.map((s) => <option key={s}>{s}</option>)}</select></Field>
            <Field label="Priority"><select className="crm-in" data-testid="export-priority" value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })}><option value="">All priorities</option>{(settings.priorities || []).map((s) => <option key={s}>{s}</option>)}</select></Field>
            <Field label="City"><input className="crm-in" data-testid="export-city" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} /></Field>
          </div>
          <button onClick={download} disabled={busy} data-testid="export-csv" className="crm-btn crm-btn-primary"><Download size={15} /> Download CSV</button>
          <p className="text-[11px] text-[#5F6E64] mt-2">The export only includes leads your role can see.</p>
        </Card>
      </div>
    </div>
  );
}
